import { useMemo } from 'react';
import { bindActionCreators, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import { authApi } from './api/auth';
import { gameApi } from './api/game';
import balanceReducer, {
  BALANCE_SLICE_KEY,
  balanceActions,
} from './slices/balanceSlice';
import betReducer, { BET_SLICE_KEY, betActions } from './slices/betSlice';
import betValuesReducer, {
  BET_VALUES_SLICE_KEY,
  betValuesActions,
} from './slices/betValuesSlice';
import gameReducer, { GAME_SLICE_KEY, gameActions } from './slices/gameSlice';
import marketReducer, {
  MARKET_SLICE_KEY,
  marketActions,
} from './slices/marketSlice';
import overlayReducer, {
  OVERLAY_SLICE_KEY,
  overlayActions,
} from './slices/overlaySlice';
import phaserReducer, {
  PHASER_SLICE_KEY,
  phaserActions,
} from './slices/phaserSlice';
import roundReducer, {
  ROUND_SLICE_KEY,
  roundActions,
} from './slices/roundSlice';

export const store = configureStore({
  reducer: {
    [BALANCE_SLICE_KEY]: balanceReducer,
    [BET_SLICE_KEY]: betReducer,
    [BET_VALUES_SLICE_KEY]: betValuesReducer,
    [GAME_SLICE_KEY]: gameReducer,
    [MARKET_SLICE_KEY]: marketReducer,
    [OVERLAY_SLICE_KEY]: overlayReducer,
    [PHASER_SLICE_KEY]: phaserReducer,
    [ROUND_SLICE_KEY]: roundReducer,
    [authApi.reducerPath]: authApi.reducer,
    [gameApi.reducerPath]: gameApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(authApi.middleware, gameApi.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type RootStore = typeof store;
export type AppDispatch = typeof store.dispatch;
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

setupListeners(store.dispatch);

export function useActions() {
  const dispatch = useDispatch();
  return useMemo(
    () =>
      bindActionCreators(
        {
          ...balanceActions,
          ...betActions,
          ...betValuesActions,
          ...gameActions,
          ...marketActions,
          ...overlayActions,
          ...phaserActions,
          ...roundActions,
        },
        dispatch
      ),
    [dispatch]
  );
}
